import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { OnPremResponseProps, ResourceRequestsProps } from "../resourceRequestInterface";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";
import { changeStatus, checkNebulaStatus, getAdminNotificationAPI, getAdminNotificationCountAPI, revokeRequest, updateApp, updateFolder, updateGroupTypeStatus, updateUrl, updateWhiteswanAccess } from "../Api/resourcerequestapi";
import {
  isMultiApprovalRequest,
} from "../../MultiApprovals/Api/multiapprovalapi";
import MultiApprovalProgressPanel from "../../MultiApprovals/MultiApprovalProgressPanel";
import MultiApprovalGroupedHistory from "../../MultiApprovals/MultiApprovalGroupedHistory";
import MultiApprovalListSummary from "../../MultiApprovals/MultiApprovalListSummary";
import { useMultiApprovalWorkflow } from "../hooks/useMultiApprovalWorkflow";
import { format } from "date-fns";
import { Card, CardContent } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import {
  AlertTriangle,
  AppWindow,
  Calendar,
  CheckCircle2,
  Clock,
  Eye,
  Folder,
  Link,
  MessageSquare,
  Monitor,
  RotateCcw,
  Server,
  Shield,
  User,
  XCircle
} from "lucide-react";
import { Button } from "../../../ui/button";
import { getStatusColor } from "../utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../ui/dialog";
import { Skeleton } from "../../interfaces/CommonInterfaces";
import { formatDate } from "../../formatTimeAgo";
import ConfirmationDialog from "../../Endpoints/ConfirmationDialog";
import { confirmNebulaChange } from "../../Endpoints/Api/EndpointApi";
import { RESOURCE_REQUEST_POLL_MS } from "../resourceRequestPolling";

export default function OnPremResourceRequests({
  computerName = "",
  searchTerm = "",
  statusFilter = "",
  startDate,
  endDate,
  activeTab
}: ResourceRequestsProps) {
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState<OnPremResponseProps[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<OnPremResponseProps | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<OnPremResponseProps | null>(null);
  const tenantName = useMemo(() => myLocalStorage.getItem("latestTenant")?.tenantName || "", []);
  const fetchInFlightRef = useRef(false);

  const { email, role } = useUserStore((state) => state.user);

  const {
    detailsWorkflow,
    openDetailsWorkflow,
    closeDetailsWorkflow,
    canUserApprove,
    canUserDecline,
    canUserDeclineDetails,
    getWorkflowForRequest,
  } = useMultiApprovalWorkflow(notifications);

  const fetchOnPremCount = useCallback(async () => {
    const response = await getAdminNotificationCountAPI(
      computerName,
      searchTerm,
      statusFilter.toUpperCase(),
      startDate ? format(startDate, "yyyy-MM-dd") : "",
      endDate ? format(endDate, "yyyy-MM-dd") : "",
      tenantName,
      page,
      email,
      role,
    );
    setTotalItems(response || 0);
  }, [computerName, searchTerm, statusFilter, startDate, endDate, tenantName, page, email, role]);

  const fetchAdminDeviceOnPremNotification = useCallback(async (isFirst = true) => {
    if (isFirst) {
      setIsLoading(true);
    }
    try {
      const response: OnPremResponseProps[] = await getAdminNotificationAPI(
        computerName,
        searchTerm,
        statusFilter.toUpperCase(),
        startDate ? format(startDate, "yyyy-MM-dd") : "",
        endDate ? format(endDate, "yyyy-MM-dd") : "",
        tenantName,
        page - 1,
        email,
        role,
      );
      setNotifications(response);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }, [computerName, searchTerm, statusFilter, startDate, endDate, tenantName, page, email, role]);

  const refreshRequests = useCallback(async (isFirst = true) => {
    if (!tenantName || !email || !role) {
      return;
    }
    if (fetchInFlightRef.current) {
      return;
    }

    fetchInFlightRef.current = true;
    try {
      await Promise.all([
        fetchOnPremCount(),
        fetchAdminDeviceOnPremNotification(isFirst),
      ]);
    } finally {
      fetchInFlightRef.current = false;
    }
  }, [tenantName, email, role, fetchOnPremCount, fetchAdminDeviceOnPremNotification]);

  const handleViewDetails = (request: OnPremResponseProps) => {
    setSelectedRequest(request);
    setIsDetailsOpen(true);
    openDetailsWorkflow(request);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "PENDING": return <Clock className="w-4 h-4" />;
      case "APPROVED": return <CheckCircle2 className="w-4 h-4" />;
      case "DECLINED": return <XCircle className="w-4 h-4" />;
      case "REVOKED": return <RotateCcw className="w-4 h-4" />;
      case "EXPIRED": return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category?.toUpperCase()) {
      case "SESSION": return <Monitor className="w-4 h-4 text-blue-500" />;
      case "APP": return <AppWindow className="w-4 h-4 text-purple-500" />;
      case "FOLDER": return <Folder className="w-4 h-4 text-yellow-500" />;
      case "URL": return <Link className="w-4 h-4 text-green-500" />;
      case "SYSTEM": return <Server className="w-4 h-4 text-gray-500" />;
      default: return <Monitor className="w-4 h-4 text-blue-500" />;
    }
  };

  const getCategoryIconLeft = (category: string) => {
    switch (category?.toUpperCase()) {
      case "SESSION": return <Monitor className="w-3.5 h-3.5 text-gray-500" />;
      case "APP": return <AppWindow className="w-3.5 h-3.5 text-gray-500" />;
      case "FOLDER": return <Folder className="w-3.5 h-3.5 text-gray-500" />;
      case "URL": return <Link className="w-3.5 h-3.5 text-gray-500" />;
      case "SYSTEM": return <Server className="w-3.5 h-3.5 text-gray-500" />;
      default: return <Monitor className="w-3.5 h-3.5 text-gray-500" />;
    }
  };


  const handleApproveClick = async (notification: any) => {
    const isNebulaDisabled = await checkNebulaStatus(notification.computerId);
    if (!isNebulaDisabled && notification.resourceName === "Whiteswan Access") {
      setSelectedNotification(notification);
      setOpenConfirmDialog(true);
    } else {
      handleRequest("APPROVED", notification.id, notification);

    }
  };


  const handleConfirmNebulaEnable = async () => {
    if (!selectedNotification) return;

    const success = await confirmNebulaChange({ deviceId: String(selectedNotification.computerId), enableNebula: true });
    if (success) {
      handleRequest("APPROVED", selectedNotification.id, selectedNotification);

    }
    setOpenConfirmDialog(false);
    setSelectedNotification(null);
  };


  const handleRequest = async (
    status: string,
    id: number,
    notification: OnPremResponseProps
  ) => {
    try {
      setLoadingId(id);
      const updatedRequest = await changeStatus(status, id) as OnPremResponseProps | undefined;

      if (status === "APPROVED" && updatedRequest?.requestStatus === "APPROVED") {
        await submitUpdateGroupStatus(notification);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingId(null);
    }

    await refreshRequests(false);
  };


  const updateWhiteswanAccessGroup = async (notification: OnPremResponseProps) => {
    const groupStatus = notification.selectedTime ? "DYNAMIC" : "ENABLED";

    try {
      let payload = {
        userId: notification.userId,
        computerId: notification.computerId,
        groupStatus,
        selectedTime: notification.selectedTime * 60,
      }

      await updateWhiteswanAccess(payload, notification.tenantName);
    } catch (error) {
      console.error(error);
    }
  };

  const updateGroupStatus = async (notification: OnPremResponseProps) => {
    const groupStatus = notification.selectedTime ? "DYNAMIC" : "ENABLED";

    const commonPayload = {
      userId: notification.userId,
      groupStatus,
      selectedTime: notification.selectedTime * 60,
      email: email,
      role: role,
      fromAccessRequest: true,
    };

    try {
      if (notification.resourceType === "GROUP") {

        let payload = {
          ...commonPayload,
          groupId: notification.resourceId,
        }
        await updateGroupTypeStatus(payload, notification.tenantName, notification.computerId)

      } else if (notification.resourceType === "FOLDER") {

        let payload = {
          ...commonPayload,
          folderId: notification.resourceId,
          computerId: notification.computerId,
        }

        await updateFolder(payload, notification.tenantName);
      } else if (notification.resourceType === "APP") {

        let payload = {
          ...commonPayload,
          appId: notification.resourceId,
          publisherId: notification.publisherId,
        }

        await updateApp(payload, notification.tenantName);

      } else if (notification.resourceType === "URL") {


        let payload = {
          ...commonPayload,
          urlId: notification.resourceId,
          computerId: notification.computerId,
        }

        await updateUrl(payload, notification.tenantName);
      }
    } catch (err) {
      console.error("Error updating group status:", err);
    }
  };



  const submitUpdateGroupStatus = async (notification: OnPremResponseProps) => {
    if (notification?.isWhiteswanAccess) {
      await updateWhiteswanAccessGroup(notification);
    } else {
      await updateGroupStatus(notification);
    }
  };


  const handleRevoke = async (accessId: number) => {
    await revokeRequest("onprem", accessId)
    await refreshRequests(false);
  }

  useEffect(() => {
    if (!tenantName || !email || !role) {
      return;
    }

    void refreshRequests(true);

    if (activeTab !== "onprem") {
      return;
    }

    const interval = setInterval(() => {
      void refreshRequests(false);
    }, RESOURCE_REQUEST_POLL_MS);

    return () => clearInterval(interval);
  }, [
    computerName,
    searchTerm,
    statusFilter,
    startDate,
    endDate,
    tenantName,
    page,
    email,
    role,
    activeTab,
    refreshRequests,
  ]);

  const totalPages = Math.ceil(totalItems / 15);
  return (
    <div className="space-y-1">
      {isLoading ? <>
        {[...Array(3)].map((_, idx) => (
          <Card key={idx} className="border-slate-200 dark:border-slate-700">
            <CardContent className="p-4 animate-pulse">
              <div className="flex justify-between items-center mb-3">
                <Skeleton className="h-4 w-32 rounded" />
                <Skeleton className="h-6 w-20 rounded" />
              </div>
              <Skeleton className="h-3 w-full rounded mb-2" />
              <Skeleton className="h-3 w-3/4 rounded mb-2" />
              <Skeleton className="h-3 w-1/2 rounded" />
              <div className="mt-4 flex justify-between">
                <Skeleton className="h-6 w-28 rounded" />
                <Skeleton className="h-6 w-28 rounded" />
              </div>
            </CardContent>
          </Card>
        ))}
      </> : notifications.length > 0 ? (
        notifications.map((request) => (
          <Card
            key={request.id}
            className="transition-all duration-200 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
            data-testid={`onprem-request-card-${request.id}`}
          >
            <CardContent className="p-2">
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-1">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          {getCategoryIconLeft(request.resourceName)}
                          <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">{request.resourceName === "Whiteswan Access" ? "Connect Access" : request.resourceName}</h3>
                        </div>
                        <Badge className={`border text-xs px-1.5 py-0.5 ${getStatusColor(request.requestStatus)}`}>
                          <div className="flex items-center gap-1">
                            {getStatusIcon(request.requestStatus)}
                            {request.requestStatus}
                          </div>
                        </Badge>
                      </div>
                      <p className="flex items-center gap-2 text-sm break-words text-slate-600 dark:text-slate-300">
                        <User className="w-4 h-4" />
                        {request.requestedBy}
                        <span className="text-slate-400">•</span>
                        {formatDate(request.createdAt)}
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(request)}
                      className="h-auto px-2 py-1 text-xs"
                      data-testid={`button-details-onprem-${request.id}`}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Details
                    </Button>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
                    <div className="flex flex-col">
                      <label className="font-medium text-slate-500 dark:text-slate-400">Computer</label>
                      <p className="break-words font-medium text-slate-700 dark:text-slate-200">{request.computerName}</p>
                    </div>
                    <div className="flex flex-col">
                      <label className="font-medium text-slate-500 dark:text-slate-400">Category</label>
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(request.resourceType)}
                        <p className="break-words font-medium text-slate-700 dark:text-slate-200">{request.resourceType}</p>
                      </div>
                    </div>
                    {request.privilegeLevel === "PERMANENT" && <div className="flex flex-col">
                      <label className="font-medium text-slate-500 dark:text-slate-400">Access Type</label>
                      <p className="break-words  font-medium text-slate-700 dark:text-slate-200">Permanent</p>
                    </div>}

                    {request.schedules.length === 0 && request.selectedTime !== 0 && <div className="flex flex-col">
                      <label className="font-medium text-slate-500 dark:text-slate-400">Duration</label>
                      <p className="text-slate-700 font-medium dark:text-slate-200">
                        {request.privilegeLevel === "PERMANENT" ? "Permanent" : `${request.selectedTime} min`}
                      </p>
                    </div>}

                  </div>

                  {/* Connection Parameters */}
                  <div className="grid grid-cols-1 gap-4 p-3 text-sm border rounded-lg md:grid-cols-2 bg-slate-50 dark:bg-slate-800/50">


                    {request.resourceType !== "GROUP" ? <>
                      <div className="flex flex-col">
                        <label className="font-medium text-slate-500 dark:text-slate-400">Machine User</label>
                        <p className=" break-words text-slate-700 dark:text-slate-200">{request.username ? request.username : "-"}</p>
                      </div>
                    </> : <>
                      {request.isManualDevice ? <div className="flex flex-col">
                        <label className="font-medium text-slate-500 dark:text-slate-400">Connection Via</label>
                        <p className=" break-words text-slate-700 dark:text-slate-200">Configuration</p>
                      </div> : !request.connectionLabel &&
                      <div className="flex flex-col">
                        <label className="font-medium text-slate-500 dark:text-slate-400">Connection Via</label>
                        <p className="break-words text-slate-700 dark:text-slate-200">
                          {request.machineUser
                            ? "Machine User"
                            : request.connectionMode === "generic"
                              ? "JIT Access"
                              : request.connectionMode
                                ? request.connectionMode.replace("_", " ")
                                : "Manual"}
                        </p>
                      </div>}

                    </>}
                    {request.machineUser && request.machineUser !== "false" && request.resourceType === "GROUP" && (
                      <div className="flex flex-col">
                        <label className="font-medium text-slate-500 dark:text-slate-400">Machine Username</label>
                        <p className="break-words text-slate-700 dark:text-slate-200">{request.machineUser}</p>
                      </div>
                    )}
                    {request.connectionUser && (
                      <div className="flex flex-col">
                        <label className="font-medium text-slate-500 dark:text-slate-400">Connection Username</label>
                        <p className="break-words text-slate-700 dark:text-slate-200">{request.connectionUser}</p>
                      </div>
                    )}
                  </div>

                  {(() => {
                    const workflow = getWorkflowForRequest(request.id);
                    return isMultiApprovalRequest(workflow) ? (
                      <MultiApprovalListSummary workflow={workflow} />
                    ) : null;
                  })()}

                  {request.schedules?.length > 0 && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Scheduled Access</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <label className="text-blue-600 dark:text-blue-400 font-medium">Start Time</label>
                          <p className="text-blue-800 dark:text-blue-200">{formatDate(request.schedules[0].scheduledStartTime)}</p>
                        </div>
                        <div>
                          <label className="text-blue-600 dark:text-blue-400 font-medium">End Time</label>
                          <p className="text-blue-800 dark:text-blue-200">{formatDate(request.schedules[0].scheduledEndTime)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {((request.approvalEmailId?.length ?? 0) > 0 || request.revokedBy) && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <div className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-2">Admin Actions</div>
                      <div className="space-y-1 text-sm">
                        {request.approvalEmailId.length > 0 && (
                          <p className="text-green-700 dark:text-green-300">
                            ✓ Last Approved by {request.approvalEmailId[0]} on {formatDate(request.createdAt)}
                          </p>
                        )}

                        {request.revokedBy && (
                          <p className="text-purple-700 dark:text-purple-300">
                            ⊝ Revoked by {request.revokedBy} on {formatDate(request.updatedAt || request.accessTimeOverAt || request.createdAt)}
                          </p>
                        )}
                      </div>
                    </div>
                  )}



                  {/* Actions */}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                    <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                      <MessageSquare className="w-3 h-3" />
                      {request.requestMessage}
                    </div>
                    <div className="flex items-center gap-2">
                      {request.requestStatus === "PENDING" && (
                        <>
                          {role !== "TENANT_USER" ? (
                            <>
                              <Button
                                size="sm"
                                disabled={loadingId === request.id || !canUserApprove(request.id)}
                                className="text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                                onClick={() => {
                                  handleApproveClick(request)
                                }}
                              >
                                {loadingId === request.id ? (
                                  <>
                                    <svg
                                      className="animate-spin w-4 h-4 mr-1 text-white"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                      ></path>
                                    </svg>
                                    Approving...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                disabled={!canUserDecline(request.id)}
                                className="disabled:opacity-50"
                                onClick={() => handleRequest("DECLINE", request.id, request)}
                              >
                                <XCircle className="w-4 h-4 mr-1" /> Decline
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                className="text-white bg-green-600 hover:bg-green-700"
                                onClick={() => setIsDetailsOpen?.(false)}
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                              </Button>
                              <Button size="sm" variant="outline" className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                                onClick={() => { handleRevoke(request.id) }}>
                                <RotateCcw className="w-4 h-4 mr-1" /> {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}
                              </Button>
                            </>
                          )}
                        </>
                      )}

                      {request.requestStatus === "APPROVED" && (
                        <Button size="sm" variant="outline" className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                          onClick={() => { handleRevoke(request.id) }}>
                          <RotateCcw className="w-4 h-4 mr-1" /> {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      ) : (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-12 text-center">
            <Server className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
            <h3 className="mb-2 text-lg font-medium text-slate-600 dark:text-slate-300">No endpoint requests found</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No access requests found for endpoint resources
            </p>
          </CardContent>
        </Card>
      )
      }

      {totalPages > 1 && (
        <div className="flex items-center justify-between py-4">
          <div className="text-sm text-slate-600 dark:text-slate-300">
            Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, totalItems)} of {totalItems}
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              data-testid="button-prev-page-onprem"
            >
              Previous
            </Button>

            {/* Dynamic Page Numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
              const pageNum = startPage + i;

              return (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPage(pageNum)}
                  className={pageNum === page ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                  data-testid={`page-onprem-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}

            {/* Next Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              data-testid="button-next-page-onprem"
            >
              Next
            </Button>
          </div>
        </div>
      )}


      {/* Request Details Modal */}
      <Dialog
        open={isDetailsOpen}
        onOpenChange={(open) => {
          setIsDetailsOpen(open);
          if (!open) {
            closeDetailsWorkflow();
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Shield className="w-5 h-5" />
              On-Premises Access Request Details
            </DialogTitle>
            <DialogDescription>
              Complete information for request #{selectedRequest?.id}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6 overflow-y-auto max-h-96">
              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                <div className="flex flex-col">
                  <label className="font-medium text-slate-500 dark:text-slate-400">Request ID</label>
                  <p className="font-mono break-words text-slate-700 dark:text-slate-200">{selectedRequest.id}</p>
                </div>
                <div className="flex flex-col">
                  <label className="text-slate-500 dark:text-slate-400 font-medium">Status</label>
                  <Badge className={`${getStatusColor(selectedRequest.requestStatus)} mt-1 `}>
                    {selectedRequest.requestStatus}
                  </Badge>
                </div>
                <div className="flex flex-col">
                  <label className="font-medium text-slate-500 dark:text-slate-400">Requested By</label>
                  <p className="break-words text-slate-700 dark:text-slate-200">{selectedRequest.requestedBy}</p>
                </div>
                <div className="flex flex-col">
                  <label className="font-medium text-slate-500 dark:text-slate-400">Created At</label>
                  <p className="text-slate-700 dark:text-slate-200">{formatDate(selectedRequest.createdAt)}</p>
                </div>
                <div className="flex flex-col">
                  <label className="font-medium text-slate-500 dark:text-slate-400">Computer Name </label>
                  <p className="break-words text-slate-700 dark:text-slate-200">{selectedRequest.computerName}</p>
                </div>
                {selectedRequest.isManualDevice ? <div className="flex flex-col">
                  <label className="font-medium text-slate-500 dark:text-slate-400">Connection Via</label>
                  <p className=" break-words text-slate-700 dark:text-slate-200">Configuration</p>
                </div> : !selectedRequest.connectionLabel && <div className="flex flex-col">

                  <label className="font-medium text-slate-500 dark:text-slate-400">Connection Via</label>

                  <p className="break-words text-slate-700 dark:text-slate-200">
                    {selectedRequest.machineUser
                      ? "Machine User"
                      : selectedRequest.connectionMode === "generic"
                        ? "JIT Access"
                        : selectedRequest.connectionMode
                          ? selectedRequest.connectionMode.replace("_", " ")
                          : "Manual"}
                  </p>

                </div>}

                {selectedRequest.machineUser && selectedRequest.machineUser !== "false" && (
                  <div className="flex flex-col">
                    <label className="font-medium text-slate-500 dark:text-slate-400">Machine Username</label>
                    <p className="break-words text-slate-700 dark:text-slate-200">{selectedRequest.machineUser}</p>
                  </div>
                )}


                {selectedRequest.connectionUser && (
                  <div className="flex flex-col">
                    <label className="font-medium text-slate-500 dark:text-slate-400">Connection Username</label>
                    <p className="break-words text-slate-700 dark:text-slate-200">{selectedRequest.connectionUser}</p>
                  </div>
                )}
              </div>

              {/* Admin Action Details */}

              {detailsWorkflow?.groupedApprovals?.some((group) => group.approvals.length > 0) ? (
                <MultiApprovalGroupedHistory groupedApprovals={detailsWorkflow.groupedApprovals} />
              ) : (selectedRequest.requestStatus === "APPROVED" ||
                (selectedRequest.approvalEmailId?.length ?? 0) > 0) ? (
                <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-300">Approval Details</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <label className="text-green-600 dark:text-green-400 font-medium">Approved By</label>
                      <p className="text-green-700 dark:text-green-300">{selectedRequest.approvalEmailId?.[0]}</p>
                    </div>
                    <div>
                      <label className="text-green-600 dark:text-green-400 font-medium">Approved At</label>
                      <p className="text-green-700 dark:text-green-300">{formatDate(selectedRequest.updatedAt ?? selectedRequest.accessTimeOverAt ?? selectedRequest.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ) : null}

              {selectedRequest.requestStatus === "PENDING" && isMultiApprovalRequest(detailsWorkflow) && (
                <MultiApprovalProgressPanel ruleProgress={detailsWorkflow.ruleProgress} />
              )}

              {selectedRequest.requestStatus === "DECLINED" && (
                <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <span className="text-sm font-medium text-red-700 dark:text-red-300">Decline Details</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">

                    <div>
                      <label className="text-red-600 dark:text-red-400 font-medium">Declined At</label>
                      <p className="text-red-700 dark:text-red-300">{formatDate(selectedRequest.updatedAt ?? selectedRequest.accessTimeOverAt ?? selectedRequest.createdAt)}</p>
                    </div>
                  </div>
                </div>
              )}

              {selectedRequest.requestStatus === "REVOKED" && selectedRequest.revokedBy && (
                <div className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2 mb-2">
                    <RotateCcw className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Revoke Details</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <label className="text-purple-600 dark:text-purple-400 font-medium">Revoked By</label>
                      <p className="text-purple-700 dark:text-purple-300">{selectedRequest.revokedBy}</p>
                    </div>
                    <div>
                      <label className="text-purple-600 dark:text-purple-400 font-medium">Revoked At</label>
                      <p className="text-purple-700 dark:text-purple-300">{formatDate(selectedRequest.updatedAt ?? selectedRequest.accessTimeOverAt ?? selectedRequest.createdAt)}</p>                    </div>
                  </div>
                </div>
              )}

              {/* Business Justification */}
              <div className="flex flex-col">
                <label className="font-medium text-slate-500 dark:text-slate-400">Business Justification</label>
                <p className="p-3 mt-1 break-words rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200">{selectedRequest.requestMessage}</p>
              </div>

              {/* Admin Action Buttons */}
              {selectedRequest.requestStatus === "PENDING" ? (
                role !== "TENANT_USER" ? (
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
                      Close
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={!canUserDeclineDetails()}
                      className="disabled:opacity-50"
                      onClick={() => {
                        handleRequest("DECLINE", selectedRequest.id, selectedRequest);
                        setIsDetailsOpen(false);
                      }}
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Decline
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                      disabled={loadingId === selectedRequest.id || !canUserApprove(selectedRequest.id)}
                      onClick={() => {
                        handleApproveClick(selectedRequest);
                      }}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  </div>
                ) : (
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => setIsDetailsOpen(false)}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                    </Button>
                  </div>
                )
              ) : null}

            </div>
          )}
        </DialogContent>
      </Dialog>


      <ConfirmationDialog
        open={openConfirmDialog}
        onOpenChange={setOpenConfirmDialog}
        title={"Enable Connectivity to Approve Request?"}
        description={"Currently connectivity is disabled for this device. Would you like to enable it and proceed with approval?"}
        confirmText="Confirm"
        cancelText="Cancel"
        variant={"warning"}
        onConfirm={() => {
          handleConfirmNebulaEnable();
          setOpenConfirmDialog(false);
          setSelectedNotification(null);
        }}
      />

    </div >
  );
}
